"use client";

import { useEffect } from "react";

type PinTarget = {
  track?: string;
  target: string;
  top: number;
};

type SafariFixedScrollPinProps = {
  rootId: string;
  desktop: PinTarget;
  mobile?: PinTarget;
};

/**
 * Safari can repaint a complex sticky scene a fraction behind its scrolling thread, which makes
 * the whole scene appear to shake even when every animated child is composited. During the hold,
 * pin that scene to the viewport itself. The existing CSS view timeline still owns all motion.
 * Other browsers keep the ordinary sticky implementation.
 */
export function SafariFixedScrollPin({ rootId, desktop, mobile }: SafariFixedScrollPinProps) {
  const desktopTarget = desktop.target;
  const desktopTop = desktop.top;
  const desktopTrack = desktop.track;
  const mobileTarget = mobile?.target;
  const mobileTop = mobile?.top;
  const mobileTrack = mobile?.track;

  useEffect(() => {
    if (!document.documentElement.classList.contains("ownly-apple-webkit")) return;

    const root = document.getElementById(rootId);
    if (!root || !CSS.supports("animation-timeline: view()")) return;

    const media = matchMedia("(max-width: 899px)");
    const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let currentTarget: HTMLElement | null = null;

    const clearTarget = () => {
      if (!currentTarget) return;
      delete currentTarget.dataset.safariPin;
      currentTarget.style.removeProperty("--safari-pin-left");
      currentTarget.style.removeProperty("--safari-pin-width");
      currentTarget = null;
    };

    const update = () => {
      frame = 0;
      if (reducedMotion.matches) {
        clearTarget();
        return;
      }
      const useMobile = media.matches && mobileTarget !== undefined && mobileTop !== undefined;
      const trackSelector = useMobile ? mobileTrack : desktopTrack;
      const targetSelector = useMobile ? mobileTarget : desktopTarget;
      const top = useMobile ? mobileTop : desktopTop;
      const track = trackSelector
        ? root.querySelector<HTMLElement>(trackSelector)
        : root;
      const target = root.querySelector<HTMLElement>(targetSelector);
      if (!track || !target) return;

      if (target !== currentTarget) {
        clearTarget();
        currentTarget = target;
      }

      const trackRect = track.getBoundingClientRect();
      const targetHeight = target.offsetHeight;
      target.dataset.safariPin =
        trackRect.top > top
          ? "before"
          : trackRect.bottom - targetHeight <= top
            ? "after"
            : "active";

      if (target.dataset.safariPin === "active") {
        target.style.setProperty("--safari-pin-left", `${trackRect.left}px`);
        target.style.setProperty("--safari-pin-width", `${trackRect.width}px`);
      }
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    addEventListener("scroll", schedule, { passive: true });
    addEventListener("resize", schedule);
    addEventListener("pageshow", schedule);
    media.addEventListener("change", schedule);
    reducedMotion.addEventListener("change", schedule);

    return () => {
      removeEventListener("scroll", schedule);
      removeEventListener("resize", schedule);
      removeEventListener("pageshow", schedule);
      media.removeEventListener("change", schedule);
      reducedMotion.removeEventListener("change", schedule);
      if (frame) cancelAnimationFrame(frame);
      clearTarget();
    };
  }, [
    desktopTarget,
    desktopTop,
    desktopTrack,
    mobileTarget,
    mobileTop,
    mobileTrack,
    rootId,
  ]);

  return null;
}
